import { DEFAULT_TIMEZONE } from "@/lib/constants";
import { dayKeyToDate, formatInTz, formatTimeRange, toDayKey } from "@/utils/format-datetime";
import { getSlotRoleLabel } from "@/utils/functions/get-slot-role-label";

/**
 * Turning availability slots into the days an employee can sign up for.
 *
 * Lifted out of `routes/app/project/[id]/layout.tsx`, where it sat inside a 700-line component as
 * an eighty-line `useMemo` — the only place in the app that could work out what someone is allowed
 * to apply to. Ontdek needs the same answer to offer signing up from the list, and this logic is
 * too load-bearing to have a second copy of.
 *
 * Everything here is timezone-sensitive and had no tests. That combination on the path an employee
 * uses to get work is the reason this is its own step: the extraction is behaviour-for-behaviour
 * identical, and now it is pinned.
 */

/** De standen die betekenen: deze dag is van jou. */
export const CLAIMING_STATUSES = ["ACCEPTED", "PENDING", "RESERVE", "CONSUMED"] as const;

export type SignupClaim = (typeof CLAIMING_STATUSES)[number];

function toClaim(status: string | null | undefined): SignupClaim | null {
  return CLAIMING_STATUSES.includes(status as SignupClaim) ? (status as SignupClaim) : null;
}

type SlotLike = {
  id: string;
  startAt: string;
  endAt: string;
  stats?: { isReserveSlot?: boolean } | null;
  position?: { name?: string | null } | null;
  userFunction?: { name?: string | null } | null;
};

type ApplicationLike = {
  status?: string | null;
  startAt: string;
  /**
   * De dienst waarop je je aanmeldde. Ontbreekt hij, dan is alleen de dag bekend.
   *
   * `unknown` en niet `string`: de gegenereerde client typt dit veld als `object`, omdat de DTO in
   * de api een nullable veld zonder expliciete `type` op zijn `@ApiProperty` heeft (zie de
   * Swagger-paragraaf in `api/CLAUDE.md`). Elders in de app staat daar een `as unknown as string`
   * voor in de plaats; hier wordt het gewoon nagekeken.
   */
  availabilitySlotId?: unknown;
};

export type SignupShift = {
  id: string;
  title: string;
  timeRange: string;
  date: string;
  isReserveSlot: boolean;
  /** Jouw aanmelding op deze dienst, of `null`. */
  claim: SignupClaim | null;
};

export type SignupDateGroup = {
  date: string;
  label: string;
  shifts: SignupShift[];
  /**
   * Jouw aanmelding op deze dag, of `null` als de dag nog vrij is.
   *
   * Een dag met een aanmelding is niet hetzelfde als een dag die weg mag. Je meldt je één keer per
   * dag aan, dus er valt niets meer te kiezen -- maar er valt wél iets te zien, en dat is precies
   * wat er ontbrak: wie zich aanmeldde voor dag 1 zag die dag verdwijnen en stond op dag 2, alsof
   * er niets gebeurd was. Wie alleen de open dagen wil, filtert met `openSignupDateGroups`.
   */
  claim: SignupClaim | null;
};

/** The calendar day a moment falls on, in the timezone the planning is written in. */
export function signupDayKey(moment: string): string {
  return toDayKey(moment, DEFAULT_TIMEZONE);
}

/**
 * De dagen van dit project, elk met de diensten die erop staan en met jouw eigen stand erbij.
 *
 * Een dag waarop je al een aanmelding hebt blijft in de lijst staan, gemarkeerd. Hij viel er eerder
 * uit -- je meldt je één keer per dag aan, dus er valt niets te kiezen -- maar daarmee viel ook het
 * bewijs weg dat je je had aangemeld: op een project van meerdere dagen verdween dag 1 onder je
 * handen en stond je op dag 2. `CONSUMED` telt als aanmelding, ook al is het inmiddels een dienst:
 * de dag is nog steeds van jou.
 *
 * Grouped and labelled in `DEFAULT_TIMEZONE` rather than the device's. A night shift that starts at
 * 23:30 belongs to the evening it started, and a phone set to another timezone should not move it
 * to the next day.
 */
export function buildSignupDateGroups<TSlot extends SlotLike>(
  slots: TSlot[] | undefined,
  applications: ApplicationLike[] | undefined,
): SignupDateGroup[] {
  if (!slots?.length) {
    return [];
  }

  const claimByDay = new Map<string, SignupClaim>();
  const claimBySlot = new Map<string, SignupClaim>();

  for (const application of applications ?? []) {
    const claim = toClaim(application.status);

    if (!claim) {
      continue;
    }

    const day = signupDayKey(application.startAt);

    // Eén aanmelding per dag; komt er toch een tweede langs, dan houdt de eerste stand -- twee
    // standen op één dag is geen vraag die dit scherm hoort te beantwoorden.
    if (!claimByDay.has(day)) {
      claimByDay.set(day, claim);
    }

    if (typeof application.availabilitySlotId === "string") {
      claimBySlot.set(application.availabilitySlotId, claim);
    }
  }

  const byDay = new Map<string, TSlot[]>();

  for (const slot of slots) {
    const day = signupDayKey(slot.startAt);

    byDay.set(day, [...(byDay.get(day) ?? []), slot]);
  }

  return [...byDay.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([day, daySlots]) => ({
      date: day,
      // `new Date("2026-08-21")` is middernacht UTC en valt westelijk van Greenwich terug naar de
      // 20e — precies de dagkop die boven de verkeerde dag stond. `dayKeyToDate` levert de echte
      // middernacht in de planningstijdzone.
      label: formatInTz(dayKeyToDate(day, DEFAULT_TIMEZONE), {
        weekday: "short",
        day: "numeric",
        month: "short",
      }),
      claim: claimByDay.get(day) ?? null,
      shifts: daySlots.map((slot) => ({
        id: slot.id,
        // Position name ("Bardienst") wins over the generic function name (#373).
        title: getSlotRoleLabel(slot),
        timeRange: formatTimeRange(slot.startAt, slot.endAt, { timeZone: DEFAULT_TIMEZONE }),
        date: day,
        isReserveSlot: slot.stats?.isReserveSlot ?? false,
        claim: claimBySlot.get(slot.id) ?? null,
      })),
    }));
}

/**
 * Alleen de dagen waarop je je nog kunt aanmelden.
 *
 * Voor de plekken die een keuze aanbieden en niet een overzicht tonen -- de aanmeldsheet op Ontdek
 * opent op één dag, en een dag die al van jou is heeft daar niets te bieden.
 */
export function openSignupDateGroups(groups: SignupDateGroup[]): SignupDateGroup[] {
  return groups.filter((group) => group.claim === null);
}
